import { motion } from "framer-motion";
import { Award, Compass, Shield, Linkedin, Instagram } from "lucide-react";
import ritaImg from "@/assets/rita-galmarini.png";
import juanPabloImg from "@/assets/juan-pablo-barrios.png";

const values = [
  {
    icon: Award,
    title: "Excelencia",
    description: "Cada proyecto es tratado con la precisión que tu trayectoria merece."
  },
  {
    icon: Compass,
    title: "Estrategia",
    description: "Decisiones fundamentadas en metodología probada globalmente."
  },
  {
    icon: Shield,
    title: "Confidencialidad",
    description: "Tu legado empresarial protegido bajo los más altos estándares."
  }
];

const founders = [
  {
    name: "Rita Galmarini",
    role: "CO-FOUNDER & CEO",
    image: ritaImg,
    bio: "Estratega de Marcas y Negocios, Coach de comunicación persuasiva.",
    description: "Con más de 16 años de trayectoria, Rita ha transformado la comunicación de líderes y organizaciones en activos de alto valor percibido.",
    highlight: "Creadora del método Sincronía Persuasiva, diseñado para alinear la identidad profunda del líder con la visión y estrategia comercial del negocio. Especialista en Marca Personal para Directivos, Rita entrena a líderes para que su comunicación deje de ser operativa y pase a ser una herramienta de influencia global y poder de negociación.",
    linkedin: "https://www.linkedin.com/in/ritagalmarini/",
    instagram: "https://www.instagram.com/ritagalmarini"
  },
  {
    name: "Juan Pablo Barrios",
    role: "CO-FOUNDER & CCO",
    image: juanPabloImg,
    bio: "Estratega de Marcas y Consultor de Negocios.",
    description: "Especialista en arquitectura marcaria y diseño estratégico, Juan Pablo ha liderado proyectos transformando marcas en íconos de prestigio.",
    highlight: "Su enfoque combina narrativas de alto valor y ecosistemas evolutivos que maximizan la reputación en mercados competitivos. Su pasión por \"artificar\" marcas eleva los elementos visuales y conceptuales a herramientas clave de diferenciación comercial, transformando la identidad en un activo estratégico.",
    linkedin: "https://www.linkedin.com/in/juanpablobarrios/",
    instagram: "https://www.instagram.com/jp_barrios/"
  }
];

const AboutUs = () => {
  return (
    <section className="relative overflow-hidden" id="quienes-somos">
      {/* First part with light background */}
      <div className="py-24 md:py-32 bg-background">
        <div className="container px-4">
          {/* Section header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-semibold tracking-widest uppercase mb-4 block">
              Quiénes Somos
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              We are <span className="text-gradient-gold">CORESSEN®</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Una consultora global especializada en <span className="text-foreground font-medium">Arquitectura de Reputación©</span>. 
              Transformamos empresas consolidadas con trayectoria en <span className="text-foreground font-medium">instituciones de referencia y autoridad</span> en sus mercados.
            </p>
          </motion.div>

          {/* About text + Values */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left column - About text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6 text-muted-foreground"
            >
              <p>
                Durante más de <span className="text-foreground font-semibold">13 años</span>, hemos acompañado a líderes empresariales de{" "}
                <span className="text-foreground font-semibold">Latinoamérica, Europa y Estados Unidos</span> en la construcción de marcas que trascienden. 
                Nuestra razón de ser es transformar la trayectoria de organizaciones consolidadas en sistemas de prestigio que operen con autonomía y visión global.
              </p>
              <p>
                Creamos <span className="text-foreground font-semibold">ecosistemas estratégicos</span> diseñados específicamente para maximizar el valor percibido de tu organización.
              </p>
              <p>
                Este enfoque de autor ha sido probado y validado en <span className="text-foreground font-semibold">más de 1000 proyectos a escala global</span>, 
                además de ser el motor de nuestra propia consultora. No implementamos teorías; aplicamos una{" "}
                <span className="text-foreground font-semibold">metodología de resultados reales y tangibles</span>.
              </p>
              <p>
                Proyectamos marcas que hablan el lenguaje del éxito en cualquier mercado. Diseñamos la identidad y la estrategia necesarias para que tu empresa sea la{" "}
                <span className="text-foreground font-semibold">opción preferida de los clientes más exigentes</span>.
              </p>
            </motion.div>

            {/* Right column - Values */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-4"
            >
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="p-6 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <value.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-semibold mb-1">{value.title}</h3>
                      <p className="text-muted-foreground text-sm">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Leadership section with dark navy background */}
      <div className="py-20 md:py-24" style={{ background: "linear-gradient(180deg, hsl(220 69% 2%), hsl(217 61% 8%))" }}>
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{ color: "#7ab7fd" }}>
              Liderazgo
            </span>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              Los Arquitectos de tu <span style={{ color: "#7ab7fd" }}>Reputación</span>
            </h3>
          </motion.div>

          {/* Founders grid */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {founders.map((founder, index) => (
              <motion.div
                key={founder.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="group"
              >
                <div className="flex flex-col md:flex-row gap-6 lg:gap-8">
                  {/* Photo */}
                  <div className="relative shrink-0">
                    <div className="w-48 h-64 md:w-56 md:h-72 rounded-xl overflow-hidden border-2 border-white/15 group-hover:border-[#7ab7fd]/50 transition-colors">
                      <img
                        src={founder.image}
                        alt={founder.name}
                        className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                      />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <h4 className="font-display text-2xl font-bold mb-1 text-white">{founder.name}</h4>
                    <p className="text-sm font-semibold tracking-wider mb-4" style={{ color: "#7ab7fd" }}>{founder.role}</p>
                    
                    <p className="text-white font-medium mb-3">{founder.bio}</p>
                    <p className="text-white/70 text-sm mb-4">{founder.description}</p>
                    <p className="text-white/70 text-sm leading-relaxed">
                      {founder.highlight.split(/(Sincronía Persuasiva|Marca Personal para Directivos|narrativas de alto valor|"artificar" marcas|activo estratégico|íconos de prestigio)/g).map((part, i) => 
                        ["Sincronía Persuasiva", "Marca Personal para Directivos", "narrativas de alto valor", "\"artificar\" marcas", "activo estratégico", "íconos de prestigio"].includes(part) 
                          ? <span key={i} className="text-white font-medium">{part}</span> 
                          : part
                      )}
                    </p>

                    {/* Social links */}
                    <div className="flex gap-4 mt-6">
                      <a
                        href={founder.linkedin}
                        className="text-white/60 hover:text-[#7ab7fd] transition-colors"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Linkedin className="w-5 h-5" />
                      </a>
                      <a
                        href={founder.instagram}
                        className="text-white/60 hover:text-[#7ab7fd] transition-colors"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
